"use client"

import { useTranslations } from "next-intl"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { ImageIcon, X } from "lucide-react"
import { useMediaQuery } from "@/hooks/use-media-query"
import { useSwapReorder } from "@/hooks/use-swap-reorder"
import { asRawImageRef, rawImageRefValue, resolveStorageUrl, type RawImageRef } from "@/lib/storage/public-url"
import { CARD_SM, imagePresetUrl } from "@/lib/images/presets"

/**
 * Structural twin of `edit-listing-dialog.tsx::PhotoItem` (the dialog imports
 * this editor, so the type cannot be shared without a cycle). `preview` is
 * BOTH display and persistence — see the dialog's declaration for the full
 * rationale; branded `RawImageRef` on both sides so the two stay assignable
 * AND a resolved absolute URL cannot enter either.
 */
export interface PhotoItem {
  file: File | null
  preview: RawImageRef
  photoId: string
}

interface SimplePhotoEditorProps {
  initialPhotos: PhotoItem[]
  onPhotosChangeAction: (photos: PhotoItem[]) => void
  showTitle?: boolean
}

export interface SimplePhotoEditorRef {
  updateParentWithCurrentOrder: () => void
  getCurrentPhotosInOrder: () => PhotoItem[]
}

const convertHeicToPreview = async (file: File): Promise<string> => {
  try {
    const isHeic =
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      file.name.toLowerCase().endsWith(".heic") ||
      file.name.toLowerCase().endsWith(".heif")

    if (!isHeic) return URL.createObjectURL(file)
    if (typeof window === "undefined") return URL.createObjectURL(file)

    const { heicTo } = await import("heic-to")
    const convertedBlob = await heicTo({ blob: file, type: "image/jpeg", quality: 0.8 })
    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob
    return URL.createObjectURL(blob)
  } catch {
    return URL.createObjectURL(file)
  }
}

const validateFile = (file: File): { valid: boolean; error?: string } => {
  const MAX_FILE_SIZE = 10 * 1024 * 1024
  const ALLOWED_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/heic",
    "image/heif",
    "image/svg+xml",
    "image/bmp",
  ]

  if (file.size > MAX_FILE_SIZE) return { valid: false, error: `File "${file.name}" is too large (max 10MB)` }

  const isAllowedType =
    ALLOWED_TYPES.includes(file.type) || file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif")

  if (!isAllowedType && !file.type.startsWith("image/")) return { valid: false, error: `File "${file.name}" is not a valid image type` }

  return { valid: true }
}

const safeId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Site-relative (`/asset.png`) previews -> absolute, for `<img>`.
 *
 * Deliberately does NOT resolve a bucket-relative STORAGE path: `preview` is
 * the value the edit dialog writes back into `listings.image_urls`
 * (`uploadImages` pushes `photo.preview` verbatim), so rewriting it here would
 * put absolute Supabase URLs back into the DB and undo phase C. The storage
 * resolve happens at the `<img src>` sinks instead, where it cannot leak into
 * persisted state.
 */
const toAbsolutePreview = (ref: RawImageRef): RawImageRef => {
  const preview = rawImageRefValue(ref)
  if (typeof window === "undefined") return ref
  if (preview.startsWith("http") || preview.startsWith("blob:")) return ref
  if (preview.startsWith("/")) return asRawImageRef(`${window.location.origin}${preview}`)
  return ref
}

export const SimplePhotoEditor = forwardRef<SimplePhotoEditorRef, SimplePhotoEditorProps>(
  ({ initialPhotos, onPhotosChangeAction, showTitle = false }, ref) => {
    const tLC = useTranslations("listingCreation")
    const fileInputRef = useRef<HTMLInputElement>(null)
    const createdBlobUrlsRef = useRef<Set<string>>(new Set())

    const [photoItems, setPhotoItems] = useState<PhotoItem[]>([])
    const photoItemsRef = useRef<PhotoItem[]>([])
    photoItemsRef.current = photoItems

    const [isDragActive, setIsDragActive] = useState(false)
    const [processingCount, setProcessingCount] = useState(0)

    const isMobile = !useMediaQuery("(min-width: 768px)")

    const syncFromInitial = useCallback(
      (incoming: PhotoItem[]) => {
        const next = (incoming ?? [])
          .filter((p) => p && rawImageRefValue(p.preview)?.trim().length > 0 && typeof p.photoId === "string" && p.photoId.length > 0)
          .map((p) => ({ ...p, preview: toAbsolutePreview(p.preview) }))
        setPhotoItems(next)
        onPhotosChangeAction(next)
      },
      [onPhotosChangeAction]
    )

    useEffect(() => {
      const incoming = Array.isArray(initialPhotos) ? initialPhotos : []
      const incomingIds = incoming.map((p) => p?.photoId).join("|")
      const currentIds = photoItemsRef.current.map((p) => p.photoId).join("|")
      if (incomingIds !== currentIds) syncFromInitial(incoming)
    }, [initialPhotos, syncFromInitial])

    const revokeIfCreated = useCallback((ref: RawImageRef) => {
      const preview = rawImageRefValue(ref)
      if (!preview.startsWith("blob:")) return
      if (!createdBlobUrlsRef.current.has(preview)) return
      try {
        URL.revokeObjectURL(preview)
      } finally {
        createdBlobUrlsRef.current.delete(preview)
      }
    }, [])

    useEffect(() => {
      return () => {
        Array.from(createdBlobUrlsRef.current).forEach((url) => {
          try {
            URL.revokeObjectURL(url)
          } catch {
          } finally {
            createdBlobUrlsRef.current.delete(url)
          }
        })
      }
    }, [])

    const processFiles = useCallback(
      async (files: File[]) => {
        const validFiles: File[] = []
        for (const file of files) {
          const v = validateFile(file)
          if (v.valid) validFiles.push(file)
        }

        const currentCount = photoItemsRef.current.length
        const remaining = Math.max(0, 10 - currentCount)
        const filesToProcess = validFiles.slice(0, remaining)
        if (filesToProcess.length === 0) return

        setProcessingCount(filesToProcess.length)
        try {
          for (const file of filesToProcess) {
            const preview = await convertHeicToPreview(file)
            if (preview.startsWith("blob:")) createdBlobUrlsRef.current.add(preview)
            const newPhoto: PhotoItem = {
              file,
              // Client-only `blob:` ref — never resolved, and the save path
              // uploads the `File` instead of persisting this value.
              preview: asRawImageRef(preview),
              photoId: `photo-${safeId()}-${file.name.replace(/[^a-zA-Z0-9]/g, "")}`,
            }
            setPhotoItems((prev) => {
              const next = [...prev, newPhoto]
              onPhotosChangeAction(next)
              return next
            })
          }
        } finally {
          setProcessingCount(0)
        }
      },
      [onPhotosChangeAction]
    )

    const handleDelete = useCallback(
      (photoId: string) => {
        setPhotoItems((prev) => {
          const toDelete = prev.find((p) => p.photoId === photoId)
          const next = prev.filter((p) => p.photoId !== photoId)
          if (toDelete) revokeIfCreated(toDelete.preview)
          onPhotosChangeAction(next)
          return next
        })
      },
      [onPhotosChangeAction, revokeIfCreated]
    )

    const { containerRef, containerProps, getItemProps, dragIndex, overIndex, touchGhost } = useSwapReorder<PhotoItem>({
      items: photoItems,
      setItems: setPhotoItems,
      getId: (p) => p.photoId,
      onReorder: (next) => {
        photoItemsRef.current = next
        onPhotosChangeAction(next)
      },
      touchGhost: true,
    })

    const touchGhostPhoto = useMemo(() => {
      if (!touchGhost) return null
      return photoItems.find((p) => p.photoId === touchGhost.id) ?? null
    }, [touchGhost, photoItems])

    const updateParentWithCurrentOrder = useCallback(() => {
      onPhotosChangeAction(photoItemsRef.current)
    }, [onPhotosChangeAction])

    const getCurrentPhotosInOrder = useCallback(() => {
      return photoItemsRef.current
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        updateParentWithCurrentOrder,
        getCurrentPhotosInOrder,
      }),
      [updateParentWithCurrentOrder, getCurrentPhotosInOrder]
    )

    return (
      <div className="w-full">
        <div className={`mb-2 flex items-center ${showTitle ? "justify-between" : "justify-end"}`}>
          {showTitle ? <h3 className="text-lg font-semibold">{tLC("productPhotos")}</h3> : null}
          <span className="text-sm text-muted-foreground">{photoItems.length}/10</span>
        </div>

        {photoItems.length > 1 ? (
          <div className="mb-3 px-3 py-2 bg-muted/50 rounded-lg border">
            <span className="text-sm text-muted-foreground">You can drag photos to reorder them</span>
          </div>
        ) : null}

        <div data-reorder-container onTouchMove={containerProps.onTouchMove} onTouchEnd={containerProps.onTouchEnd}>
          <div
            ref={containerRef}
            className="grid grid-cols-3 gap-2.5 md:grid-cols-4 md:gap-4"
            onDragOver={containerProps.onDragOver}
            onDrop={containerProps.onDrop}
          >
            {photoItems.map((p, i) => {
              const props = getItemProps(i)
              return (
                <div
                  key={p.photoId}
                  {...props}
                  className={`relative overflow-hidden rounded-xl border-2 will-change-transform cursor-grab active:cursor-grabbing ${
                    dragIndex === i ? "scale-[0.98] border-foreground/40 opacity-30" : overIndex === i ? "border-foreground ring-2 ring-foreground/20" : "border-border"
                  }`}
                  style={{ aspectRatio: "1 / 1" }}
                >
                  {/* 3-up (4-up on md) reorder tile. `p.preview` is a `blob:`
                      URL for a freshly picked File and a bucket path when
                      EDITING an existing listing — the second case downloaded
                      the full upload into a ~120 px cell. `imagePresetUrl`
                      passes `blob:` straight through, so one call covers both. */}
                  <img src={imagePresetUrl(resolveStorageUrl(p.preview), CARD_SM)} alt="" className="pointer-events-none size-full object-cover" draggable={false} />
                  <button
                    type="button"
                    data-reorder-no-drag
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleDelete(p.photoId)
                    }}
                    className="absolute right-2 top-2 flex size-7 items-center justify-center rounded-full bg-neutral-900/70 text-white backdrop-blur-sm transition-opacity active:opacity-70"
                    aria-label="Remove photo"
                  >
                    <X className="size-4" strokeWidth={2.5} />
                  </button>
                </div>
              )
            })}

            {processingCount > 0
              ? Array.from({ length: processingCount }).map((_, idx) => (
                  <div
                    key={`skeleton-${idx}`}
                    className="relative overflow-hidden rounded-xl border-2 border-border bg-muted/60 animate-pulse"
                    style={{ aspectRatio: "1 / 1" }}
                    data-reorder-id={`__skeleton__${idx}`}
                  >
                    <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                      <div className="h-6 w-6 rounded-full border-2 border-muted-foreground/40 border-t-muted-foreground animate-spin" />
                    </div>
                  </div>
                ))
              : null}
          </div>

          {touchGhost && touchGhostPhoto ? (
            <div
              className="pointer-events-none fixed left-0 top-0 z-9999 rounded-xl shadow-2xl"
              style={{
                width: touchGhost.w,
                height: touchGhost.h,
                transform: `translate3d(${touchGhost.x}px, ${touchGhost.y}px, 0) scale(1.03)`,
              }}
            >
              {/* The drag ghost is the same tile at the same size — same preset,
                  so it repaints from the browser cache instead of refetching. */}
              <img src={imagePresetUrl(resolveStorageUrl(touchGhostPhoto.preview), CARD_SM)} alt="" className="size-full rounded-xl object-cover" draggable={false} />
            </div>
          ) : null}
        </div>

        {photoItems.length < 10 ? (
          <div
            className={`
              border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
              transition-colors duration-200 flex flex-col items-center justify-center
              ${isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/20"}
              ${photoItems.length > 0 ? "mt-4" : ""}
            `}
            style={{ minHeight: "120px" }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault()
              setIsDragActive(true)
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              setIsDragActive(false)
            }}
            onDrop={async (e) => {
              e.preventDefault()
              setIsDragActive(false)
              const files = Array.from(e.dataTransfer.files || [])
              await processFiles(files)
            }}
          >
            <ImageIcon className="w-10 h-10 text-muted-foreground mb-2" />
            <p className="text-muted-foreground text-sm">
              {tLC("tapToUpload")}
            </p>
          </div>
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={async (e) => {
            const files = Array.from(e.target.files || [])
            await processFiles(files)
            e.target.value = ""
          }}
        />
      </div>
    )
  }
)

SimplePhotoEditor.displayName = "SimplePhotoEditor"

